import './FilterOptionRangeSlider.scss';

import { registryComponent } from '@boost-sd/components-registry/registry';
import { InnerHTMLRenderer, useLatest } from '@boost-sd/core-js';
import useTransformCurrency from '@hooks/useTransformCurrency';
import type { FilterOptionType } from '@providers/FilterProvider';
import { FILTER_OPTION_TYPE } from '@providers/FilterProvider';
import type { ModifiersType } from '@utils';
import { buildNumberLabel, createClsNameMap, formatMoney, mapModifiers, roundAmount } from '@utils';
import type { API, Options } from 'nouislider';
import noUiSlider, { PipsMode } from 'nouislider';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { FilterOptionProps } from '../FilterOption';
import FilterOption from '../FilterOption';

/**
 *  We use noUiSlider library: https://refreshless.com/nouislider/
 */

export const clsNameMap = createClsNameMap({
  modifiers: ['style-1'],
  elements: {
    slider: createClsNameMap(),
    'range-amount': createClsNameMap(),
    'input-wrapper': createClsNameMap(),
    unit: createClsNameMap(),
    input: createClsNameMap({
      modifiers: ['right'],
    }),
    'input-split': createClsNameMap(),
  },
})('filter-option-range-slider');

type State = {
  min: number | string;
  max: number | string;
};

export type FilterOptionRangeSliderProps<FilterOption extends FilterOptionType> = {
  option: FilterOption;
  filterOptionProps?: Partial<FilterOptionProps>;
  sliderRange: number;
  sliderStep: number;
  modifiers?: ModifiersType<typeof clsNameMap>;
  values: {
    min: number;
    max: number;
  };
  optionIsFiltering?: Array<string>;
  settings: {
    rangeSlidersSingleHandle: Array<string>;
    formatPipsRange: Array<{
      node: number;
      symbol: string;
      fix: number;
      suffix: boolean;
    }>;
    removePriceDecimal: boolean;
    precisionFormatLabelSlider: number;
    rateCurrency: number;
    unitCurrency: string;
  };
  onSelect: (value: string) => void;
};

const FilterOptionRangeSlider = <FilterOption extends FilterOptionType>({
  option,
  filterOptionProps,
  sliderRange,
  sliderStep,
  values,
  modifiers = ['style-1'],
  optionIsFiltering,
  settings,
  onSelect,
}: FilterOptionRangeSliderProps<FilterOption>) => {
  //cover data api return sliderRange is string 0
  if (typeof sliderRange !== 'number') {
    sliderRange = Number(sliderRange) || 1;
  }

  if (isNaN(sliderRange)) {
    sliderRange = 1;
  }

  if (values && values.min && values.max) {
    values.min = Math.floor(values.min);
    values.max = Math.ceil(values.max);
  }

  const {
    rangeSlidersSingleHandle,
    formatPipsRange,
    removePriceDecimal,
    precisionFormatLabelSlider,
    rateCurrency,
    unitCurrency,
  } = settings;

  const { getAmountRangeOrigin, getAmountRangeTranslate } = useTransformCurrency(rateCurrency);

  const isNumberRangeSlider = !Array.isArray(values) && values?.min != null && values?.max != null;

  const normalizedValues = useMemo(() => {
    return isNumberRangeSlider ? values : { min: 0, max: 0 };
  }, [isNumberRangeSlider, values]);

  const normalizedValuesTranslated = useMemo(() => {
    return getAmountRangeTranslate(normalizedValues);
  }, [normalizedValues, getAmountRangeTranslate]);

  const valueSliderFirstLoad = useMemo(() => {
    if (optionIsFiltering?.[0]?.includes(':')) {
      const [min, max] = optionIsFiltering[0]?.split(':') || [];

      return {
        min: Number(min || normalizedValuesTranslated.min),
        max: Number(max || normalizedValuesTranslated.max),
      };
    }

    return {
      min: normalizedValuesTranslated.min,
      max: normalizedValuesTranslated.max,
    };
  }, [optionIsFiltering, option, normalizedValuesTranslated]);

  const rangeSliderElmRef = useRef<HTMLDivElement | null>(null);
  const noUISliderRef = useRef<API | null>(null);
  const minAmountInputRef = useRef<HTMLInputElement | null>(null);
  const maxAmountInputRef = useRef<HTMLInputElement | null>(null);

  const amountOriginRef = useRef<State>(valueSliderFirstLoad);
  const [state, setState] = useState<State>(valueSliderFirstLoad);

  //order 1: If max and min values too close together, recalculate sliderRange
  if (normalizedValues.max - normalizedValues.min < sliderStep * sliderRange) {
    sliderRange =
      parseInt(((normalizedValues.max - normalizedValues.min) / sliderStep).toFixed()) || 1;
  }

  //order 2: check sliderStep > max - min re-calc sliderStep.
  if (
    sliderStep >
    Number(normalizedValuesTranslated.max) - Number(normalizedValuesTranslated.min)
  ) {
    sliderStep =
      (Number(normalizedValuesTranslated.max) - Number(normalizedValuesTranslated.min)) /
      sliderRange;
  }

  // cover data api return stepNumber not type Number
  if (typeof sliderStep !== 'number') {
    sliderStep = Number(sliderStep) || 1;
  }

  if (isNaN(sliderStep)) {
    sliderStep = 1;
  }

  const handleChangeSliderState = (params: Partial<State>, notCallApi?: boolean) => {
    const nextState = {
      ...state,
      ...params,
    };

    if (
      Number.isNaN(nextState.min) ||
      nextState.min < normalizedValuesTranslated.min ||
      nextState.min > normalizedValuesTranslated.max
    ) {
      // origin min value
      nextState.min = normalizedValuesTranslated.min;
    }

    if (
      Number.isNaN(nextState.max) ||
      nextState.max > normalizedValuesTranslated.max ||
      nextState.max < normalizedValuesTranslated.min
    ) {
      // origin max value
      nextState.max = normalizedValuesTranslated.max;
    }

    const minAmountInput = minAmountInputRef.current;
    const maxAmountInput = maxAmountInputRef.current;

    if (Number(nextState.min) > Number(nextState.max)) {
      if (minAmountInput) {
        minAmountInput.value = roundAmount(+state.min, precisionFormatLabelSlider).toString();
      }

      if (maxAmountInput) {
        maxAmountInput.value = roundAmount(+state.max, precisionFormatLabelSlider).toString();
      }
      return;
    }

    setState(nextState);

    if (minAmountInput) {
      minAmountInput.value = roundAmount(+nextState.min, precisionFormatLabelSlider).toString();
    }

    if (maxAmountInput) {
      maxAmountInput.value = roundAmount(+nextState.max, precisionFormatLabelSlider).toString();
    }

    const amountOrigin = getAmountRangeOrigin(nextState);
    amountOriginRef.current = amountOrigin;

    !notCallApi &&
      onSelect(
        `${roundAmount(amountOrigin.min, precisionFormatLabelSlider, true)}:${roundAmount(
          amountOrigin.max,
          precisionFormatLabelSlider
        )}`
      );
  };

  const handleChangeSliderStateRef = useLatest(handleChangeSliderState);

  const isSingleRange = useMemo(() => {
    const singleInputRanges: string[] = rangeSlidersSingleHandle || [];

    return singleInputRanges.includes(option.filterOptionId);
  }, [rangeSlidersSingleHandle, option.filterOptionId]);

  const hideTextBoxes = () => {
    let _hidden = true;

    if (isNumberRangeSlider && values.min !== values.max) {
      _hidden = Boolean(option?.hideTextBoxes);
    }

    return _hidden;
  };

  const onAmountInputBlurOrKeyUp = (
    event: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (
      event.type === 'blur' ||
      (event.type === 'keyup' && (event as React.KeyboardEvent).key === 'Enter')
    ) {
      const target = event.currentTarget;
      const inputType = target.getAttribute('data-type');
      if (inputType === 'min') {
        const min = isNumberRangeSlider ? Number(target.value) : target.value;

        handleChangeSliderState({
          min: roundAmount(+min, precisionFormatLabelSlider),
        });
      }

      if (inputType === 'max') {
        const max = isNumberRangeSlider ? Number(target.value) : target.value;

        handleChangeSliderState({
          max: roundAmount(+max, precisionFormatLabelSlider),
        });
      }
    }
  };

  const isFilterPrice = [FILTER_OPTION_TYPE.PRICE, FILTER_OPTION_TYPE.VARIANTS_PRICE].includes(
    option.filterType
  );

  const initSlider = () => {
    const rangeSliderElm = rangeSliderElmRef.current;
    if (!rangeSliderElm) return;
    if (noUISliderRef.current) {
      noUISliderRef.current.destroy();
    }

    // Create new slider
    let numberOfPips =
      (isNumberRangeSlider && Number(sliderRange) + 1) ||
      (Array.isArray(values) && values.length + 1) ||
      0;
    if (numberOfPips < 2) numberOfPips = 2;

    const pipConfig: Options['pips'] = {
      mode: PipsMode.Count,
      values: numberOfPips,
      filter: (_, type) => {
        // Only render big pip
        return type === 1 ? type : -1;
      },
      format: {
        to: function (value) {
          const MIN_VALUE_SUPPORT_FORMAT = 0.04;

          if (value <= MIN_VALUE_SUPPORT_FORMAT) return value;

          let label: string | number = '';
          const isShortenNumber = isNumberRangeSlider && Boolean(option?.shortenPipsRange);

          label = buildNumberLabel(
            value,
            isShortenNumber,
            option,
            formatPipsRange,
            removePriceDecimal,
            precisionFormatLabelSlider
          );

          return label;
        },
      },
    };

    const startRange = optionIsFiltering
      ? { min: state.min, max: state.max }
      : { min: normalizedValuesTranslated.min, max: normalizedValuesTranslated.max };

    const noUiSliderOptions = {
      connect: true,
      range: normalizedValuesTranslated,
      snap: !isNumberRangeSlider,
      step: Number(sliderStep),
      start: isSingleRange ? [startRange.min] : [startRange.min, startRange.max],
      animate: true,
      pips: pipConfig,
      animationDuration: 300,
    } as any;

    const sliderInstance = noUiSlider.create(rangeSliderElm, noUiSliderOptions);
    noUISliderRef.current = sliderInstance;

    // Set event change slider
    sliderInstance.on('change', (elmValues) => {
      const [min, max] = elmValues;

      // change -> update state change
      if (isNumberRangeSlider) {
        handleChangeSliderStateRef.current({
          min: Number(min),
          max: Number(max),
        });
      }
    });
  };

  const setDataInput = (value: State) => {
    const minAmountInput = minAmountInputRef.current;
    const maxAmountInput = maxAmountInputRef.current;

    if (minAmountInput) {
      minAmountInput.value = roundAmount(+value.min, precisionFormatLabelSlider).toString();
    }

    if (maxAmountInput) {
      maxAmountInput.value = roundAmount(+value.max, precisionFormatLabelSlider).toString();
    }
  };

  // only run when init range slider
  useEffect(initSlider, []);

  useEffect(() => {
    setDataInput(valueSliderFirstLoad);
  }, [valueSliderFirstLoad]);

  // run when state change
  useEffect(() => {
    // update range and start of slider
    noUISliderRef.current?.updateOptions(
      {
        range: {
          min: +normalizedValuesTranslated.min,
          max: +normalizedValuesTranslated.max,
        },
        start: [state.min, state.max],
        step: sliderStep,
      },
      true
    );

    const min = Math.max(+state.min, +normalizedValuesTranslated.min);
    const max = Math.min(+state.max, +normalizedValuesTranslated.max);
    setDataInput({ min, max });
  }, [state, normalizedValuesTranslated]);

  // run when reload page and filter option not selected
  useEffect(() => {
    if (!optionIsFiltering) {
      setState(() => {
        amountOriginRef.current = { ...normalizedValues };

        return {
          ...normalizedValuesTranslated,
        };
      });

      // reset slider when clear filter option
      setDataInput(normalizedValuesTranslated);
    }
  }, [
    optionIsFiltering,
    normalizedValues.min,
    normalizedValues.max,
    normalizedValuesTranslated.min,
    normalizedValuesTranslated.max,
  ]);

  // run when change currency
  useEffect(() => {
    if (!unitCurrency) return;

    const amountInput = getAmountRangeTranslate(amountOriginRef.current);
    handleChangeSliderState(amountInput, true);
  }, [rateCurrency, unitCurrency]);

  return (
    <FilterOption
      {...filterOptionProps}
      className={mapModifiers(clsNameMap, modifiers)}
      option={option}
      noList
    >
      {!hideTextBoxes() && (
        <div className={clsNameMap.elm('range-amount')}>
          <div className={clsNameMap.elm('input-wrapper')}>
            {isFilterPrice && (
              <InnerHTMLRenderer
                as='span'
                className={clsNameMap.elm('unit')}
                html={formatMoney('', unitCurrency)}
              />
            )}
            <input
              ref={minAmountInputRef}
              className={mapModifiers(clsNameMap.input, { right: Boolean(isFilterPrice) })}
              type='text'
              data-type='min'
              aria-label='Min value'
              onKeyUp={onAmountInputBlurOrKeyUp}
              onBlur={onAmountInputBlurOrKeyUp}
              defaultValue={normalizedValuesTranslated.min}
            />
          </div>
          <div className={clsNameMap.elm('input-split')}> - </div>
          <div className={clsNameMap.elm('input-wrapper')}>
            {isFilterPrice && (
              <InnerHTMLRenderer
                as='span'
                className={clsNameMap.elm('unit')}
                html={formatMoney('', unitCurrency)}
              />
            )}
            <input
              ref={maxAmountInputRef}
              className={mapModifiers(clsNameMap.input, { right: Boolean(isFilterPrice) })}
              type='text'
              data-type='max'
              aria-label='Max value'
              onKeyUp={onAmountInputBlurOrKeyUp}
              onBlur={onAmountInputBlurOrKeyUp}
              defaultValue={normalizedValuesTranslated.max}
            />
          </div>
        </div>
      )}
      <div ref={rangeSliderElmRef} className={clsNameMap.elm('slider')}></div>
    </FilterOption>
  );
};

export default registryComponent('FilterOptionRangeSlider', FilterOptionRangeSlider);
